import React, { useRef, useState } from 'react';
import './LoiAndTermsForm.scss';
import { TermsFormDisplay } from './TermsFormDisplay';
import { LoiForm } from '../../../../../../slices/marketplace/loi-form/loi-form';
import { TermForm, createEmptyTermForm } from '../../../../../../slices/marketplace/loi-form/term-form';
import { useStateSelector } from '../../../../../../store/selectors';
import createNotification from '../../../../../../utils/createNotification';
import ButtonLoader from '../../../../../../components/Layout/Buttons/ButtonLoader';

interface LoiAndTermsFormProps {
    formData: LoiForm;
    isReadonly: boolean;
    onChange: (termForm: TermForm) => void;
    errors?: Set<string>;
    opportunityId?: number;
    loiActionId?: number;
    uploadedDocumentName?: string;
    documentDetailsId?: string;
    onUploadComplete?: () => void;
    loiFileError?: boolean;
}

export const LoiAndTermsForm = (props: LoiAndTermsFormProps) => {
    const termForm: TermForm = props.formData.termForm ?? createEmptyTermForm();
    const axios = useStateSelector((state) => state.core.axios);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [localUploadedFileName, setLocalUploadedFileName] = useState<string | undefined>(undefined);

    const uploadedFileName = localUploadedFileName ?? props.uploadedDocumentName;

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !axios || !props.opportunityId || !props.loiActionId) {
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            const response = await axios.post(
                `/api/Marketplace/${props.opportunityId}/MarketplaceOpportunityDocuments/UploadActionDocument/${props.loiActionId}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            if (response.status !== 200) {
                const message = (response as any)?.response?.data?.detail;
                createNotification(
                    message ?? 'An error occurred while uploading the file',
                    'error'
                );
            } else {
                setLocalUploadedFileName(file.name);
                createNotification('File uploaded successfully', 'success');
                props.onUploadComplete?.();
            }
        } catch {
            createNotification('An error occurred while uploading the file', 'error');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const canUpload = !props.isReadonly && !!props.opportunityId && !!props.loiActionId;

    const handleOpenDocument = () => {
        if (!props.documentDetailsId || !props.opportunityId || !axios) return;
        axios
            .get(
                `/api/marketplace/${props.opportunityId}/MarketplaceOpportunityDocuments/${props.documentDetailsId}`
            )
            .then((response) => {
                if (response.status === 200) {
                    window.open(response.data, '_blank');
                }
            })
            .catch(() =>
                createNotification(
                    'An error occurred while opening the file.',
                    'error'
                )
            );
    };

    const isDocumentClickable = !!props.documentDetailsId && !!props.opportunityId;

    return (
        <div className="loi-and-terms-form">
            <div className={`loi-document-upload${props.isReadonly ? ' loi-document-upload--readonly' : ''}`}>
                <div className="loi-document-upload-row">
                    <div className="loi-document-upload-label">{props.isReadonly ? 'LOI File' : 'LOI File Upload'}</div>
                    {canUpload && (
                        <>
                            <ButtonLoader
                                buttonText="Upload File"
                                loaderButtonText=""
                                isLoading={isUploading}
                                disabled={isUploading}
                                onClick={handleUploadClick}
                                className="btn btn-primary"
                            />
                            <input
                                hidden
                                ref={fileInputRef}
                                type="file"
                                name="loi-document"
                                accept=".pdf"
                                onChange={handleFileChange}
                            />
                        </>
                    )}
                    {uploadedFileName && isDocumentClickable && (
                        <div
                            className="loi-document-upload-filename loi-document-upload-filename--clickable"
                            onClick={handleOpenDocument}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleOpenDocument();
                                }
                            }}
                            role="button"
                            tabIndex={0}>
                            {uploadedFileName}
                        </div>
                    )}
                </div>
                {props.loiFileError && (
                    <div className="loi-document-upload-error text-danger">LOI file is required</div>
                )}
            </div>
            <div className="loi-and-terms-form-content">
                <TermsFormDisplay
                    termForm={termForm}
                    legalIssues={props.formData.legalIssues ?? []}
                    additionalCovenants={props.formData.additionalCovenants ?? []}
                    readOnly={props.isReadonly}
                    onChange={(updated) => props.onChange(updated)}
                    errors={props.errors}
                />
            </div>
        </div>
    );
};
